// Validation utilities for seller registration

export const validateMobileNumber = (mobile) => {
  const mobileRegex = /^[6-9]\d{9}$/;
  if (!mobile) return 'Mobile number is required';
  if (!mobileRegex.test(mobile)) return 'Please enter a valid 10-digit Indian mobile number';
  return '';
};

export const validateOTP = (otp) => {
  if (!otp) return 'OTP is required';
  if (otp.length !== 6 || !/^\d{6}$/.test(otp)) return 'OTP must be 6 digits';
  return '';
};

export const validatePAN = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!pan) return 'PAN is required';
  if (!panRegex.test(pan)) return 'Invalid PAN format (e.g., ABCDE1234F)';
  return '';
};

export const validateGSTIN = (gstin) => {
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!gstin) return 'GSTIN is required';
  if (!gstinRegex.test(gstin)) return 'Invalid GSTIN format (15 characters)';
  return '';
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) return 'Email is required';
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  return '';
};

export const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least 1 capital letter';
  if (!/[0-9]/.test(password)) return 'Password must contain at least 1 number';
  if (!/[@$!%*?&]/.test(password)) return 'Password must contain at least 1 special character (@$!%*?&)';
  return '';
};

export const validateFullName = (name) => {
  if (!name || name.trim() === '') return 'Full name is required';
  if (name.trim().length < 2) return 'Full name must be at least 2 characters';
  if (!/^[a-zA-Z\s]+$/.test(name)) return 'Name can only contain letters and spaces';
  return '';
};

export const validatePincode = (pincode) => {
  if (!pincode) return 'Pincode is required';
  if (!/^\d{6}$/.test(pincode)) return 'Pincode must be 6 digits';
  return '';
};

export const validateIFSCCode = (ifsc) => {
  // IFSC format: 4 letters (bank code) + 0 + 6 alphanumeric (branch code) = 11 chars
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  if (!ifsc) return 'IFSC code is required';
  if (ifsc.length !== 11) return 'IFSC code must be 11 characters';
  if (!ifscRegex.test(ifsc.toUpperCase())) return 'Invalid IFSC code format (e.g., SBIN0001234)';
  return '';
};

export const validateAccountNumber = (accountNumber) => {
  if (!accountNumber) return 'Account number is required';
  if (accountNumber.length < 9 || accountNumber.length > 18) {
    return 'Account number should be between 9-18 digits';
  }
  if (!/^\d+$/.test(accountNumber)) return 'Account number should contain only digits';
  return '';
};

export const validateStoreName = (storeName) => {
  if (!storeName || storeName.trim() === '') return 'Store name is required';
  if (storeName.trim().length < 3) return 'Store name must be at least 3 characters';
  if (storeName.trim().length > 50) return 'Store name must not exceed 50 characters';
  return '';
};

export const validateAddress = (address) => {
  if (!address || address.trim() === '') return 'Address is required';
  if (address.trim().length < 5) return 'Please enter a complete address';
  return '';
};

export const validateState = (state) => {
  if (!state) return 'State is required';
  return '';
};

export const validateCity = (city) => {
  if (!city || city.trim() === '') return 'City is required';
  return '';
};

export const validateDistrict = (district) => {
  if (!district || district.trim() === '') return 'District is required';
  return '';
};

export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  return '';
};

// Batch validation
export const validateMobileVerification = (mobileNumber, otp) => {
  const errors = {};
  const mobileError = validateMobileNumber(mobileNumber);
  const otpError = validateOTP(otp);

  if (mobileError) errors.mobileNumber = mobileError;
  if (otpError) errors.otp = otpError;

  return errors;
};

export const validateBusinessDetails = (formData) => {
  const errors = {};

  if (formData.hasGST === null) {
    errors.hasGST = 'Please select GST status';
  }

  if (formData.hasGST === true) {
    const gstError = validateGSTIN(formData.gstNumber);
    if (gstError) errors.gstNumber = gstError;
  } else if (formData.hasGST === false && !formData.enrolmentId) {
    const panError = validatePAN(formData.panNumber);
    const nameError = validateFullName(formData.fullName);
    const emailError = validateEmail(formData.email);

    if (panError) errors.panNumber = panError;
    if (nameError) errors.fullName = nameError;
    if (emailError) errors.email = emailError;
  }

  return errors;
};

export const validatePickupAddress = (pickupAddress) => {
  const errors = {};

  if (!pickupAddress.useEnrolmentAddress) {
    const buildingError = validateRequired(pickupAddress.buildingNumber, 'Building number');
    const streetError = validateRequired(pickupAddress.street, 'Street');
    const pincodeError = validatePincode(pickupAddress.pincode);
    const stateError = validateState(pickupAddress.state);
    const cityError = validateCity(pickupAddress.city);

    if (buildingError) errors.buildingNumber = buildingError;
    if (streetError) errors.street = streetError;
    if (pincodeError) errors.pincode = pincodeError;
    if (stateError) errors.state = stateError;
    if (cityError) errors.city = cityError;
  }

  return errors;
};

export const validateBankDetails = (formData) => {
  const errors = {};

  const accountError = validateAccountNumber(formData.accountNumber);
  const confirmError = validateAccountNumber(formData.confirmAccountNumber);
  const ifscError = validateIFSCCode(formData.ifscCode);

  if (accountError) errors.accountNumber = accountError;
  if (confirmError) errors.confirmAccountNumber = confirmError;
  if (ifscError) errors.ifscCode = ifscError;

  if (formData.accountNumber && formData.confirmAccountNumber) {
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      errors.confirmAccountNumber = 'Account numbers do not match';
    }
  }

  return errors;
};

export const validateSupplierDetails = (formData) => {
  const errors = {};

  const storeError = validateStoreName(formData.storeName);
  const nameError = validateFullName(formData.supplierFullName);
  const emailError = validateEmail(formData.supplierEmail);

  if (storeError) errors.storeName = storeError;
  if (nameError) errors.supplierFullName = nameError;
  if (emailError) errors.supplierEmail = emailError;

  if (!formData.agreeToTerms) {
    errors.agreeToTerms = 'You must agree to the Supplier Agreement';
  }

  return errors;
};
